import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { AuthService } from '../../no-auth/services/auth.service';
import { SnackBarService } from '../../no-auth/services/snackbar.service';

const NO_ERROR_LOGGING = new HttpContextToken<boolean>(() => true);

@Injectable({
  providedIn: 'root',
})
export class AuthInterceptorService {
  constructor(
    private authService: AuthService,
    private afAuth: AngularFireAuth,
    private snackbar: SnackBarService,
    private logger: NGXLogger,
  ) {}

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.authService.isLoggedIn$.pipe(
      filter((isLoggedIn) => !!isLoggedIn),
      switchMap(() => this.afAuth.idToken),
      take(1),
      switchMap((token) => {
        const setHeaders = {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          Authorization: `Bearer ${token}`,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'Content-Type': req.headers.get('Content-Type') || 'application/json',
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'web-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'web-timezone-offset-minutes': `${new Date().getTimezoneOffset()}`,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'x-api-web-app': 'broker',
        };
        let ignoreContentType =
          typeof req.body === 'undefined' ||
          req.body === null ||
          req.body.toString() === '[object FormData]' ||
          req.headers.has('Content-Type');
        if (ignoreContentType) {
          delete setHeaders['Content-Type'];
        }
        const copy = req.clone({
          setHeaders,
        });
        return next.handle(copy).pipe(
          catchError((err) => {
            let showSnackbar = true;
            if (req.context.get(NO_ERROR_LOGGING)) {
              showSnackbar = false;
            }
            this.logError(err, showSnackbar);
            return throwError(err);
          }),
        );
      }),
    );
  }

  private logError(error: any, showSnackbar: boolean = true) {
    this.logger.error(error);
    if (error instanceof HttpErrorResponse && showSnackbar) {
      if (error.error.isClientError) {
        this.snackbar.showError(error.error.messageToUser);
      } else {
        this.snackbar.showError('Unexpected Error Occurred Processing Request');
      }
    }
  }
}
